import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { Margin } from '../../layout'
import { Span } from '../../typography/Body'
import { Label } from '../label'
import {
  StCharacterCounter,
  StFieldContainer,
  StInputContainer,
  StInputIcon,
  stInputErrorContainer,
} from './TextArea.styled'

import type { ChangeEvent, FormEvent, TextareaHTMLAttributes } from 'react'

type TextAreaProps = {
  label?: string
  noMargin?: boolean
  error?: string
  value?: string
  autoGrow?: boolean
  maxHeight?: number
  onChange?: (value: string) => void
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'>

const AnimatedIcon = motion(StInputIcon)
const AnimatedFieldContainer = motion(StFieldContainer)
const AnimatedFieldErrorContainer = motion(stInputErrorContainer)

const TextArea = ({
  label,
  noMargin,
  maxLength,
  error,
  value = '',
  autoGrow,
  maxHeight,
  onChange,
  ...props
}: TextAreaProps) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [charCount, setCharCount] = useState(value.length)

  // Template variables
  // ------------------------------------------------------------------------- /
  const hasError = useMemo(() => !!error, [error])

  const animatedErrorMessageProps = useMemo(
    () => ({
      initial: { opacity: 0, y: -30 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  const resizeTextarea = useCallback((textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [])

  useEffect(() => {
    if (autoGrow && inputRef.current) {
      resizeTextarea(inputRef.current)
    }
  }, [autoGrow, resizeTextarea])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    setCharCount(newValue.length)
    onChange?.(newValue)
  }

  const handleAutoResize = (event: FormEvent<HTMLTextAreaElement>) => {
    if (autoGrow) {
      resizeTextarea(event.currentTarget)
    }
  }

  return (
    <AnimatedFieldContainer noMargin={noMargin} showMaxLength={!!maxLength} $autoGrow={autoGrow}>
      {label ? (
        <Label htmlFor={props.id || props.name}>
          {label} {props.required ? '*' : null}
        </Label>
      ) : null}
      <StInputContainer error={hasError} $autoGrow={autoGrow} $maxHeight={maxHeight}>
        <textarea
          {...props}
          value={value}
          ref={inputRef}
          onChange={handleInputChange}
          onInput={handleAutoResize}
          maxLength={maxLength}
        />
        {maxLength ? (
          <StCharacterCounter>
            {charCount}/{maxLength}
          </StCharacterCounter>
        ) : null}

        <AnimatePresence mode='wait' initial={false}>
          {hasError ? (
            <AnimatedIcon
              key='error-icon'
              icon={['far', 'triangle-exclamation']}
              {...getDropInProps({
                hidden: { opacity: 0, x: 30 },
                visible: { opacity: 1, x: 0 },
                exit: { opacity: 0, x: 30 },
                transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
              })}
            />
          ) : null}
        </AnimatePresence>
      </StInputContainer>

      <AnimatePresence mode='wait'>
        {hasError && error ? (
          <AnimatedFieldErrorContainer key={error} {...animatedErrorMessageProps}>
            <Margin mt={theme.UI.Spacing.Space2} />
            <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
              {error}
            </Span>
          </AnimatedFieldErrorContainer>
        ) : null}
      </AnimatePresence>
    </AnimatedFieldContainer>
  )
}

export { TextArea }
